import { entityStore } from '~/utility/BinSentry-ui-utility';
import { css, html, LitEntity, renderIf } from '../utility/lit-entity';
import { StoreMixin } from '../utility/store-mixin';
import { sharedStyles } from '../styles/shared-styles';
import '../common/bn-icon';
import '../common/bn-modal-button';
import '../common/bn-placeholder';
import '../common/bn-surface';
import './actions/organization-add-child-modal';
import './parts/organization-bin-count-header';
import './props/organization-name';
import './props/organization-type';

class OrganizationChildrenList extends StoreMixin(LitEntity) {
  static get properties() {
    return {
      linkPath: { type: String },
      isFetching: { type: Boolean, attribute: false },
      children: { type: Array, attribute: false },
      currentOrgType: { type: String, attribute: false },
      currentOrgHref: { type: String, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        color: var(--black-base);
        margin-bottom: 1em;
      }
      bn-surface {
        padding: 0.25rem;
      }
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      a {
        display: flex;
        text-decoration: none;
        transition: .2s background-color;
        padding: 0 1rem;
        border-radius: 4px;
        color: var(--black-base)
      }
      a:hover {
        background-color: var(--hover-color);
      }
      p {
        text-align: center;
        color: inherit;
        padding: 1em;
      }
      organization-bin-count-header {
        padding-right: 2px;
        width: 60px;
        text-align: center;
        display: flex;
        justify-content: flex-end;
        align-items: center;
      }
      .name {
        padding: 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1 1;
        max-width: calc(100% - 35px);
      }
      .type {
        font-size: .8em;
        align-self: center;
      }
      .actions-container {
        display: flex;
        justify-content: flex-end;
        margin: var(--root-margin);
      }
      .multi-mill-banner {
        padding: 1em;
      }
    `];
  }

  renderEntity() {
    if (!this.entity || !this.children) {
      return html`<bn-placeholder></bn-placeholder>`;
    }

    if (this.children.length === 0) {
      return html`
        ${this._renderAddModal()}
        <bn-surface>
          <p>No organizations to display.</p>
        </bn-surface>
      `;
    }

    const showBanner = ['binsentry', 'billing-account'].includes(this.currentOrgType) && this.currentOrgHref === this.getHref('up');

    return html`
      <lit-entity .href="${this.currentOrgHref}" @update="${this._onUpdateCurrentOrg}"></lit-entity>
      ${renderIf(showBanner, () => html`
        <bn-surface class="multi-mill-banner">Your account has multiple mills. To switch between mills, use the dropdown selector in the top right of the screen.</bn-surface>
      `)}
      ${this._renderAddModal()}
      <bn-surface>
        <ul>
          ${this.children.map((child) => html`
            <li>
              <a href="${this.linkPath}?orgId=${encodeURIComponent(child.href)}">
                <organization-bin-count-header .href="${child.href}"></organization-bin-count-header>
                <div class="name">
                  <organization-name .href="${child.href}"></organization-name>
                  <span class="type subdued">
                    - <organization-type .href="${child.href}"></organization-type>
                  </span>
                </div>
              </a>
            </li>
          `)}
        </ul>
      </bn-surface>

      <bn-load-more-button
        .href="${this.href}"
        @moreloaded="${(e) => this._parseChildren(e.detail.entity)}"
      ></bn-load-more-button>
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.children = [];
    if (!entity) {
      return;
    }

    this._parseChildren(this.entity);
  }

  _onUpdateCurrentOrg(e) {
    this.currentOrgType = e.detail.entity.getSubEntity('https://api.binsentry.com/rel/organization-type').properties.name;
  }

  _stateChanged({ currentOrgHref }) {
    this.currentOrgHref = currentOrgHref;
  }

  _renderAddModal() {
    if (!this.entity?.hasActionByClass('create-organization')) {
      return html``;
    }

    return html`
      <div class="actions-container">
        <bn-modal-button button-class="button button--contained">
          <span slot="text">
            <bn-icon name="fal fa-plus"></bn-icon>
            Add Organization
          </span>
          <organization-add-child-modal
            slot="modal"
            .href="${this.href}"
            @response=${this._onResponseAddChild}
          ></organization-add-child-modal>
        </bn-modal-button>
      </div>
    `;
  }

  _onResponseAddChild() {
    entityStore.invalidateHref(this.href);
  }

  _parseChildren(entity) {
    if (!entity || !entity.entities) {
      return;
    }
    const children = entity.entities.map((sub) => sub.getLinkByRel('self'));
    this.children = [...this.children, ...children];
  }
}

window.customElements.define('organization-children-list', OrganizationChildrenList);
